import { useAuth } from 'src/auth/AuthContext';
import { Refresh } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  Alert,
} from "@mui/material";
import axios from "axios";
import { useState } from "react";
import moment from "moment";
import { useCurrentLocation } from "src/components/Routes/LocationRoute";
import InviteUserDialog from "src/components/InviteUserDialog";
import { PermissionRole } from "src/constants";
import { type MerchantUser } from "src/queries/mappers";
import {
  useInviteUser,
  useRemoveUser,
  useTeam,
  useUpdateUserRole,
  verifyUserEmail,
} from "src/queries/team";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import NewUserDetailsDialog from "src/components/NewUserDetailsDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteFromTeamConfirmationDialog from "src/components/DeleteFromTeamConfirmationDialog";

interface Column {
  id: keyof MerchantUser;
  label: string;
  numeric: boolean;
}

const columns: Column[] = [
  { id: "firstName", label: "First Name", numeric: false },
  { id: "lastName", label: "Last Name", numeric: false },
  { id: "email", label: "Email", numeric: false },
  { id: "permissionRole", label: "Role", numeric: false },
  { id: "dateCreated", label: "Date Added", numeric: false },
];

const PageLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <Container sx={{ width: "100%" }}>
      <Box
        mb={4}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h4" gutterBottom>
          Manage Team
        </Typography>
      </Box>
      <Divider sx={{ mb: 4 }} />
      <Paper elevation={3} style={{ padding: "20px" }}>
        {children}
      </Paper>
    </Container>
  );
};

const ManageTeamPage = () => {

  const { merchant } = useCurrentLocation();

  const currentUser = useAuth();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<number>();
  const [newUserRole, setNewUserRole] = useState<PermissionRole>();

  const [orderBy, setOrderBy] = useState<keyof MerchantUser>("firstName");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [isNewUserDialogOpen, setIsNewUserDialogOpen] = useState(false);
  const [pendingInvite, setPendingInvite] = useState<{
    email: string;
    role: PermissionRole;
  } | null>(null);

  const [userToRemove, setUserToRemove] = useState<MerchantUser | null>(null);
  
  // New state for notification snackbars
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState<"success" | "info" | "warning" | "error">("success");

  const {
    data: users = [],
    isPending: isTeamPending,
    isError: isTeamError,
    refetch: refetchTeam,
  } = useTeam(merchant.id);

  const inviteUser = useInviteUser();
  const updateUserRole = useUpdateUserRole();
  const removeUser = useRemoveUser();

  /**
   * `isPending` rather than `isFetching`: the old page gated on a `loading`
   * boolean that went true on every refetch, so the whole table vanished each
   * time someone was invited or removed. Now it only hides before the first
   * load, and later refreshes swap the rows in place.
   *
   * The old slice also had no `.rejected` case, so a failed fetch left
   * `loading` true forever and this page spun indefinitely. A query has an
   * error state, so that is now reachable.
   */
  const isDataLoading = isTeamPending;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property: keyof MerchantUser) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleRefresh = () => {
    refetchTeam();
  };

  const verifyUser = async (email: string) => {
    try {
      return await verifyUserEmail(email);
    } catch (error) {
      console.error("Error verifying user:", error);
      return false;
    }
  };

  const handleInviteSubmit = async (email: string, role: PermissionRole) => {
    try {
      const userExists = await verifyUser(email);

      if (userExists) {
        // Existing user - proceed with regular invite
        await handleInviteUser(email, role);
        // Show notification for existing user
        setNotificationMessage(`${email} already has an account and has been added to your team and notified via email.`);
        setNotificationType("success");
        setNotificationOpen(true);
      } else {
        // New user - open details dialog
        setPendingInvite({ email, role });
        setIsNewUserDialogOpen(true);
      }
      setIsOpen(false);
    } catch (error) {
      console.error("Error inviting user:", error);
      // Re-throw the error so it can be caught by the dialog component
      throw error;
    }
  };

  const handleInviteUser = async (
    email: string,
    role: PermissionRole,
    firstName?: string,
    lastName?: string,
    password?: string
  ) => {
    try {
      const payload = {
        email,
        role,
        ...(firstName && { firstName }),
        ...(lastName && { lastName }),
        ...(password && { password }),
      };

      // The mutation invalidates the team list, replacing the manual refetch
      // that had to follow every write.
      await inviteUser.mutateAsync({ locationId: merchant.id, ...payload });
    } catch (error) {
      console.error("Error inviting user:", error);
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 400 && error.response.data?.message) {
          throw new Error(error.response.data.message);
        } else if (error.response.status === 401) {
          throw new Error("Unauthorized. Please log in again.");
        } else if (error.response.status === 403) {
          throw new Error("You don't have permission to invite users.");
        }
      }
      throw new Error("Failed to invite user. Please try again.");
    }
  };

  const handleNewUserSubmit = async (
    firstName: string,
    lastName: string,
    password: string
  ) => {
    // Errors propagate to the dialog, which renders them next to its form.
    if (pendingInvite) {
      await handleInviteUser(
        pendingInvite.email,
        pendingInvite.role,
        firstName,
        lastName,
        password
      );
      setIsNewUserDialogOpen(false);
      setPendingInvite(null);

      setNotificationMessage(`${firstName} ${lastName} has been added to your team and notified via email.`);
      setNotificationType("success");
      setNotificationOpen(true);
    }
  };

  const handleUpdateUserRole = async (user: MerchantUser) => {
    try {
      if (user.permissionRole !== newUserRole && newUserRole !== undefined) {
        await updateUserRole.mutateAsync({
          locationId: merchant.id,
          userId: user.id,
          role: newUserRole,
        });
      }
    } finally {
      setEditingUser(undefined);
      setNewUserRole(undefined);
    }
  };

  const handleRemoveUser = async (user: MerchantUser) => {
    setUserToRemove(user);
  };

  const confirmRemoveUser = async () => {
    if (userToRemove) {
      try {
        await removeUser.mutateAsync({
          locationId: merchant.id,
          userId: userToRemove.id,
        });
      } catch (error) {
        console.error("Error removing user:", error);
      } finally {
        setUserToRemove(null);
      }
    }
  };

  const getColumnElement = (user: MerchantUser, column: Column) => {
    if (column.id === "permissionRole" && user.id === editingUser) {
      return (
        <Select
          required
          value={newUserRole ?? user.permissionRole}
          onChange={(e) => setNewUserRole(e.target.value as PermissionRole)}
        >
          <MenuItem value={PermissionRole.Staff}>
            {PermissionRole.Staff}
          </MenuItem>
          <MenuItem value={PermissionRole.Admin}>
            {PermissionRole.Admin}
          </MenuItem>
        </Select>
      );
    }

    // The mapper keeps `dateCreated` as a raw ISO string so the cache stays
    // presentation-free; the old reducer formatted it with moment before storing.
    if (column.id === "dateCreated") {
      return moment(user.dateCreated).format("DD MMM YYYY HH:mm");
    }

    return user[column.id];
  };

  const getIconButton = (user: MerchantUser) => {
    if (user.id === editingUser) {
      return (
        <Box display="flex">
          <IconButton aria-label="save" onClick={() => handleUpdateUserRole(user)}>
            <SaveIcon />
          </IconButton>
        </Box>
      );
    }

    return (
      <Box display="flex">
        {user.id !== currentUser.id && (
          <>
            <IconButton aria-label="edit" onClick={() => {
              setNewUserRole(undefined);
              setEditingUser(user.id);
            }}>
              <EditIcon />
            </IconButton>
            <IconButton
              aria-label="delete"
              onClick={() => handleRemoveUser(user)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon />
            </IconButton>
          </>
        )}
      </Box>
    );
  };

  if (isDataLoading) {
    return (
      <PageLayout>
        <Container
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "250px",
          }}
        >
          <CircularProgress />
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {isOpen && (
        <InviteUserDialog
          isOpen
          merchantName={merchant.name}
          handleClose={() => setIsOpen(false)}
          handleSubmit={handleInviteSubmit}
        />
      )}
      {isNewUserDialogOpen && pendingInvite && (
        <NewUserDetailsDialog
          isOpen={isNewUserDialogOpen}
          email={pendingInvite.email}
          handleClose={() => {
            setIsNewUserDialogOpen(false);
            setPendingInvite(null);
          }}
          handleSubmit={handleNewUserSubmit}
        />
      )}
      {userToRemove && (
        <DeleteFromTeamConfirmationDialog
          isOpen={Boolean(userToRemove)}
          title="Remove Team Member"
          message={`Are you sure you want to remove ${userToRemove.firstName} ${userToRemove.lastName} from your team?`}
          handleClose={() => setUserToRemove(null)}
          handleConfirm={confirmRemoveUser}
        />
      )}
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notificationOpen}
        autoHideDuration={6000}
        onClose={() => setNotificationOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotificationOpen(false)} 
          severity={notificationType} 
          elevation={6} 
          variant="filled"
        >
          {notificationMessage}
        </Alert>
      </Snackbar>

      <TableContainer>
        <Box display="flex" justifyContent="flex-end" p={2} gap={4}>
          <IconButton onClick={handleRefresh} disabled={isDataLoading}>
            <Refresh />
          </IconButton>
          <Button variant="contained" onClick={() => setIsOpen(true)}>
            Add user
          </Button>
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.numeric ? "right" : "left"}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => handleSort(column.id)}
                  >
                    <Typography variant="body1">{column.label}</Typography>
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell key="edit_user" align="left" />
            </TableRow>
          </TableHead>
          <TableBody>
            {users
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((user) => (
                <TableRow key={user.id}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.numeric ? "right" : "left"}
                    >
                      {getColumnElement(user, column)}
                    </TableCell>
                  ))}
                  <TableCell key="edit_user" align="left">
                    {getIconButton(user)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25, 50]}
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </PageLayout>
  );
};

export default ManageTeamPage;
